import type { ClientEnrollmentData as EnrollmentData } from '@/features/enrollment'
import { getStepFields, getCampaignConfig } from '../config/campaigns.config'

/**
 * Creates initial empty enrollment data with default values
 * This is just for frontend state initialization - actual data structure is dynamic based on campaign
 */
export const getInitialEnrollmentData = (agentName?: string): EnrollmentData => ({
  // Agent/List info
  listId: null, // ✅ Will be auto-populated from campaign's organization
  agentName: agentName || '',
  
  // Campaign selection
  selectedCampaign: undefined,
  campaignId: undefined,
  
  // Step completion tracking
  completedSteps: [],
  skippedSteps: [],
})

/**
 * Creates save step payload with required fields
 */
export const createSaveStepPayload = (
  sessionId: string | undefined, 
  stepId: string, 
  stepIndex: number, 
  stepData: any
) => ({
  sessionId,
  stepId,
  stepIndex,
  stepData,
  enrollmentType: 'CLIENT_ENROLLMENT' as const // Agent is doing client enrollment
})

/**
 * ✅ 100% DYNAMIC: Build step payload using campaign-specific field mapping
 * Works for N campaigns - Gaming, Roblox, and any future campaigns
 * NO HARDCODED MAPPINGS - Everything comes from database
 */
export const buildStepPayload = (
  stepId: string, 
  data: EnrollmentData,
  campaignId?: string
) => {
  // Get database campaign ID from multiple possible sources
  const databaseCampaignId = 
    campaignId || 
    (data as any).campaign?.id || 
    (data as any).campaignId || 
    (data as any).selectedCampaign

  if (!databaseCampaignId) {
    console.error('❌ CRITICAL: No campaign ID provided to buildStepPayload')
    
    // Return minimal payload to avoid crashes
    return {
      campaignId: null,
      selectedCampaign: null,
      completedSteps: (data as any).completedSteps || [],
      skippedSteps: (data as any).skippedSteps || [],
      error: 'No campaign ID found'
    }
  }

  // ✅ Get campaign config from database
  const campaignConfig = getCampaignConfig(databaseCampaignId)
  
  if (!campaignConfig) {
    console.error('❌ Campaign config not found in cache:', databaseCampaignId)
    
    return {
      campaignId: databaseCampaignId,
      selectedCampaign: (data as any).selectedCampaign,
      completedSteps: (data as any).completedSteps || [],
      skippedSteps: (data as any).skippedSteps || [],
      error: 'Campaign config not found'
    }
  }

  // ✅ Get fields for THIS step in THIS campaign (from database)
  const stepFields = getStepFields(databaseCampaignId, stepId)


  // Build payload with campaign-specific fields ONLY
  const payload = {
    // Campaign info - ALWAYS include
    campaignId: databaseCampaignId,
    selectedCampaign: (data as any).selectedCampaign || databaseCampaignId,
    
    // Completion tracking
    completedSteps: (data as any).completedSteps || [],
    skippedSteps: (data as any).skippedSteps || [],
    
    // ✅ Include ONLY fields that belong to THIS step in THIS campaign
    ...Object.fromEntries(
      Object.entries(data).filter(([key]) => 
        stepFields.includes(key) && // Only include fields from database mapping
        !['campaignId', 'selectedCampaign', 'completedSteps', 'skippedSteps', 'campaign'].includes(key)
      )
    )
  }

  // Log what we're sending
  const includedFields = Object.keys(payload).filter(k => 
    !['campaignId', 'selectedCampaign', 'completedSteps', 'skippedSteps'].includes(k)
  )


  // ⚠️ Warning if no fields were included (might indicate field naming mismatch)
  if (includedFields.length === 0 && stepFields.length > 0) {
    console.warn('⚠️ WARNING: No data fields included in payload!', {
      stepId,
      campaignName: campaignConfig.name,
      expectedFields: stepFields,
      availableDataKeys: Object.keys(data).filter(k => 
        !['campaignId', 'selectedCampaign', 'completedSteps', 'skippedSteps', 'campaign'].includes(k)
      ),
      diagnosis: 'Field names in enrollmentData do not match database field mapping'
    })
  }

  // Check for data corruption - nested step data
  const corruptedSteps = Object.keys(payload)
    .filter(key => key.startsWith('step'))
    .filter(stepKey => {
      const stepData = (payload as any)[stepKey]
      if (typeof stepData === 'object' && stepData !== null) {
        return Object.keys(stepData).some(nestedKey => nestedKey.startsWith('step'))
      }
      return false
    })

  if (corruptedSteps.length > 0) {
    console.error('🚨 DATA CORRUPTION DETECTED:', {
      stepId,
      campaignName: campaignConfig.name,
      corruptedSteps,
      details: corruptedSteps.reduce((acc, stepKey) => {
        acc[stepKey] = (payload as any)[stepKey]
        return acc
      }, {} as any)
    })
  }

  return payload
}

/**
 * ✅ DYNAMIC: Clone enrollment data based on campaign configuration
 * Different campaigns can have different fields preserved
 */
export const createClonedEnrollmentData = (
  baseData: EnrollmentData,
  campaignId?: string
): EnrollmentData => {
  const databaseCampaignId = campaignId || (baseData as any).campaignId
  const campaignConfig = getCampaignConfig(databaseCampaignId)


  // Start with clean initial data
  const clonedData: EnrollmentData = {
    ...getInitialEnrollmentData(),
    listId: baseData.listId,
    agentName: baseData.agentName,
    campaignId: databaseCampaignId,
    selectedCampaign: (baseData as any).selectedCampaign
  }

  if (!campaignConfig) {
    console.warn('⚠️ No campaign config found for cloning, using minimal data')
    return clonedData
  }

  // ✅ Define fields that should NEVER be copied (identity/personal info)
  const fieldsToExclude = new Set([
    // Identity fields that should NOT be copied
    'injuredPartyFirstName',
    'injuredPartyMiddleName',
    'injuredPartyLastName',
    'injuredPartySuffix',
    'injuredPartyEmail',
    'phoneNumber',
    'secondaryPhoneNumber',
    'injuredPartyDOB',
    'ssn',
    'signature',
    'clientSigned',
    'idDocumentUrl',
    'intakeFirstName', // Clear for new enrollment
    'intakeLastName',  // Clear for new enrollment
    
    // Step tracking (reset for new enrollment)
    'completedSteps',
    'skippedSteps',
    
    // Follow-up fields (reset for new enrollment)
    'submitAnotherChild',
    'makeReferral',
    'referralEmail',
    'referralEmails',
    
    // Internal metadata
    'campaign',
    'sessionId'
  ])

  // ✅ Collect all fields from campaign's field mapping
  const fieldsToPreserve = new Set<string>()
  
  Object.values(campaignConfig.fieldMapping || {}).forEach(fields => {
    fields.forEach(field => {
      if (!fieldsToExclude.has(field)) {
        fieldsToPreserve.add(field)
      }
    })
  })


  // Copy preserved fields from base data
  fieldsToPreserve.forEach(field => {
    if ((baseData as any)[field] !== undefined) {
      (clonedData as any)[field] = (baseData as any)[field]
    }
  })

  // ✅ Set defaults for new child enrollment
  if ((clonedData as any).relationToVictim !== undefined) {
    (clonedData as any).relationToVictim = 'Loved one'
  }
  
  if ((clonedData as any).familyMemberAddicted !== undefined) {
    (clonedData as any).familyMemberAddicted = 'Loved one'
  }

  return clonedData
}